const API_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.token = localStorage.getItem('movez_token') || null;
    this.refreshToken = localStorage.getItem('movez_refresh_token') || null;
    this._refreshPromise = null;
  }

  setToken(token) {
    this.token = token;
    if (token) localStorage.setItem('movez_token', token);
    else localStorage.removeItem('movez_token');
  }

  setRefreshToken(token) {
    this.refreshToken = token;
    if (token) localStorage.setItem('movez_refresh_token', token);
    else localStorage.removeItem('movez_refresh_token');
  }

  async request(endpoint, options = {}) {
    // FormData define o próprio Content-Type (com boundary); não sobrescrever.
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...options.headers,
    };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const lang = localStorage.getItem('movez-language');
    if (lang) headers['Accept-Language'] = lang;

    const response = await fetch(`${this.baseUrl}${endpoint}`, { ...options, headers });

    // 401 → tenta refresh uma vez (deduplicado).
    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this._tryRefresh();
      if (refreshed) {
        return this.request(endpoint, { ...options, _isRetry: true });
      }
      this.setToken(null);
      this.setRefreshToken(null);
    }

    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    if (!response.ok) {
      const error = new Error(data.message || data.error || `Erro ${response.status}`);
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    return data;
  }

  get(endpoint, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(query ? `${endpoint}?${query}` : endpoint);
  }
  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body: JSON.stringify(body) });
  }
  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body: JSON.stringify(body) });
  }
  patch(endpoint, body) {
    return this.request(endpoint, { method: 'PATCH', body: JSON.stringify(body) });
  }
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * Envia um arquivo para POST /uploads e retorna a URL pública.
   * Não definir Content-Type manualmente: o browser monta o boundary do FormData.
   * @param {File} file
   * @param {string} folder avatars|covers|progress|exercises|misc
   * @returns {Promise<string>} URL do arquivo
   */
  async upload(file, folder = 'misc') {
    const form = new FormData();
    form.append('file', file);
    const data = await this.request(`/uploads?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
      body: form,
    });
    return (data.data || data).url;
  }

  async _tryRefresh() {
    if (!this.refreshToken) return false;
    if (this._refreshPromise) return this._refreshPromise;

    this._refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshToken }),
        });
        if (!response.ok) return false;
        const json = await response.json();
        const data = json.data || json;
        this.setToken(data.token || data.accessToken);
        if (data.refreshToken) this.setRefreshToken(data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }
}

const api = new ApiClient();
export default api;
